"use client"

import * as React from "react"
import type { UseFormReturn } from "react-hook-form"

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query"

import type { Invoice } from "@/features/invoices/components/InvoiceTable"
import type { ApiError } from "@/lib/api/client"

import { classifyDealInvoiceStatus } from "./actions"
import {
  amendInvoice,
  createInvoice,
  deleteInvoice,
  fetchEmailPreview,
  fetchInvoice,
  finalizeInvoice,
  getInstallmentDefaults,
  getInvoiceFilterFacets,
  getNamedDiscounts,
  listInvoices,
  manualRetryInstallment,
  resendInvoice,
  searchDeals,
  sendInvoice,
  updateInvoice,
  voidInvoice,
} from "./api"
import type { CreatedByParam } from "./filters"
import { toFrontendInvoice, toFrontendInvoiceDetail } from "./mappers"
import type {
  AmendInvoiceIn,
  BackendStatus,
  CreateInvoiceForm,
  CreateInvoiceIn,
  DealSearchOut,
  DealSearchParams,
  EmailPreviewOut,
  InstallmentDefaultsOut,
  InvoiceDetail,
  InvoiceDetailOut,
  InvoiceFilterFacetsOut,
  ManualRetryOut,
  NamedDiscountOut,
  PaymentMode,
  UpdateInvoiceIn,
  VoidInvoiceIn,
} from "./types"

export interface UseInvoicesParams {
  status?: BackendStatus | BackendStatus[]
  paymentMode?: PaymentMode
  /** Repeated `?market=` values; empty/absent means "all markets". */
  markets?: string[]
  /** Lowercase ISO-4217 code; absent means "all currencies". */
  currency?: string
  createdBy?: CreatedByParam
  courseId?: string
  createdAtFrom?: string
  createdAtTo?: string
  updatedAtFrom?: string
  updatedAtTo?: string
  search?: string
  page: number
  pageSize: number
}

export interface UseInvoicesData {
  items: Invoice[]
  count: number
}

export function invoicesListKey(params: UseInvoicesParams) {
  return ["invoices", "list", params] as const
}

export function invoiceDetailKey(id: string) {
  return ["invoices", "detail", id] as const
}

export function useInvoices(
  params: UseInvoicesParams,
): UseQueryResult<UseInvoicesData, ApiError> {
  const {
    status,
    paymentMode,
    markets,
    currency,
    createdBy,
    courseId,
    createdAtFrom,
    createdAtTo,
    updatedAtFrom,
    updatedAtTo,
    search,
    page,
    pageSize,
  } = params
  return useQuery<UseInvoicesData, ApiError>({
    queryKey: invoicesListKey(params),
    queryFn: async ({ signal }) => {
      const response = await listInvoices(
        {
          status,
          payment_mode: paymentMode,
          market: markets,
          currency,
          created_by: createdBy,
          course_id: courseId,
          created_at_from: createdAtFrom,
          created_at_to: createdAtTo,
          updated_at_from: updatedAtFrom,
          updated_at_to: updatedAtTo,
          search,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
        { signal },
      )
      return {
        items: response.items.map(toFrontendInvoice),
        count: response.count,
      }
    },
    placeholderData: (prev) => prev,
  })
}

export function useInvoice(
  id: string,
  options?: { enabled?: boolean },
): UseQueryResult<InvoiceDetail, ApiError> {
  return useQuery<InvoiceDetail, ApiError>({
    queryKey: invoiceDetailKey(id),
    queryFn: async ({ signal }) => {
      const dto = await fetchInvoice(id, { signal })
      return toFrontendInvoiceDetail(dto)
    },
    // Callers can force the query off (e.g. once the invoice is being
    // deleted) so removing its cache entry can't trigger a refetch of a
    // now-gone resource.
    enabled: Boolean(id) && (options?.enabled ?? true),
    throwOnError: true,
  })
}

export interface ManualRetryInstallmentVars {
  invoiceId: string
  scheduleId: string
}

export function useManualRetryInstallment(): UseMutationResult<
  ManualRetryOut,
  ApiError,
  ManualRetryInstallmentVars
> {
  const queryClient = useQueryClient()
  return useMutation<ManualRetryOut, ApiError, ManualRetryInstallmentVars>({
    mutationFn: (vars) =>
      manualRetryInstallment(vars.invoiceId, vars.scheduleId),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: invoiceDetailKey(vars.invoiceId),
      })
    },
  })
}

export interface VoidInvoiceVars {
  id: string
  body: VoidInvoiceIn
}

export function useVoidInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  VoidInvoiceVars
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, VoidInvoiceVars>({
    mutationFn: ({ id, body }) => voidInvoice(id, body),
    onSuccess: (detail) => {
      queryClient.setQueryData(
        invoiceDetailKey(detail.id),
        toFrontendInvoiceDetail(detail),
      )
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

/**
 * Delete a DRAFT invoice (self-service path). The backend returns 204
 * with no body, so the mutation resolves to ``void``. On success we drop
 * the detail cache entry for the now-gone row and invalidate the list so
 * it disappears from every invoice view.
 */
export function useDeleteInvoice(): UseMutationResult<void, ApiError, string> {
  const queryClient = useQueryClient()
  return useMutation<void, ApiError, string>({
    mutationFn: (id) => deleteInvoice(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: invoiceDetailKey(id) })
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function invoicesByDealKey(dealId: string) {
  return ["invoices", "by-deal", dealId] as const
}

/**
 * The duplicate-invoice prompt a deal's existing open invoice calls for:
 *   - ``"void_existing"`` — an active, voidable invoice exists; the rep
 *     must void it before issuing a new one.
 *   - ``"open_existing"`` — a draft/email_failed invoice is still being
 *     worked; the rep should continue that row.
 *   - ``"none"`` — no blocking invoice (settled, abandoned, or only the
 *     form's own in-progress draft); a fresh invoice is legitimate.
 */
export type DealInvoiceResolution = "none" | "open_existing" | "void_existing"

/**
 * The existing invoice a non-``none`` resolution points at — the minimal
 * row shape the prompt + void role gate need. ``createdById`` comes from
 * ``InvoiceOut.created_by.id`` and feeds the void modal's ownership gate.
 */
export interface ExistingDealInvoice {
  id: string
  status: BackendStatus
  createdById: number
}

export interface UseOpenInvoicesForDealData {
  resolution: DealInvoiceResolution
  // The row the resolution points at, or null when ``resolution`` is
  // ``"none"``.
  existing: ExistingDealInvoice | null
  // Retained for a superset-compatible shape: ``resolution !== "none"``.
  hasOpenInvoice: boolean
  // Retained: the raw server count of open invoices for the deal (not
  // adjusted for ``excludeInvoiceId``).
  count: number
}

// The in-progress union — every status that means the customer still
// has unresolved billing on the deal. The classifier below partitions
// it into the void bucket (VOIDABLE_STATUSES) and the go-to bucket
// (SENDABLE_STATUSES). Terminal states (paid, voided, expired) are
// excluded: the deal is settled or abandoned and a fresh invoice is a
// legitimate reissue. (The backend also has refunded /
// partially_refunded; the BackendStatus union does not model them
// today and they are terminal in the same sense once added.)
const OPEN_INVOICE_STATUSES: BackendStatus[] = [
  "draft",
  "sent",
  "viewed",
  "overdue",
  "email_failed",
  "payment_failed",
]

// Shared read-query policy for every HubSpot read hook. Retry/refetch
// are off because the panel side-effects key on data identity —
// flickering through retries would flash the "HubSpot unavailable"
// alert and re-poke HubSpot on transient tab switches. Each hook
// surfaces a single deterministic error state instead.
const HUBSPOT_READ_QUERY_OPTIONS = {
  staleTime: 30_000,
  retry: false,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const

export function dealSearchKey(params: DealSearchParams) {
  // Normalise to undefined so two callers with the same effective
  // payload share a cache slot.
  return [
    "hubspot",
    "deal-search",
    {
      first_name: params.first_name || undefined,
      last_name: params.last_name || undefined,
      email: params.email || undefined,
      phone: params.phone || undefined,
      limit: params.limit,
    },
  ] as const
}

export interface UseDealSearchOptions {
  enabled?: boolean
}

export function useDealSearch(
  params: DealSearchParams,
  options: UseDealSearchOptions = {},
): UseQueryResult<DealSearchOut, ApiError> {
  const { enabled = true } = options
  return useQuery<DealSearchOut, ApiError>({
    queryKey: dealSearchKey(params),
    queryFn: ({ signal }) => searchDeals(params, { signal }),
    enabled,
    ...HUBSPOT_READ_QUERY_OPTIONS,
  })
}

export function namedDiscountsKey() {
  return ["named-discounts"] as const
}

/**
 * Fetch the active named-discount catalog for the create/edit form's
 * multi-select. The catalog is small and changes rarely, so cache it for
 * the session and serve it stale-while-revalidate.
 */
export function useNamedDiscounts(): UseQueryResult<
  NamedDiscountOut[],
  ApiError
> {
  return useQuery<NamedDiscountOut[], ApiError>({
    queryKey: namedDiscountsKey(),
    queryFn: async ({ signal }) => {
      const response = await getNamedDiscounts({ signal })
      return response.items
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function invoiceFilterFacetsKey() {
  return ["invoices", "filter-facets"] as const
}

/**
 * The market / currency values present in the caller's visible invoices,
 * feeding the two list-page filter pickers. Facets change rarely (a new
 * market or currency only appears when an invoice is issued in one), so
 * this carries a long staleTime; the list itself never waits on it.
 */
export function useInvoiceFilterFacets(): UseQueryResult<
  InvoiceFilterFacetsOut,
  ApiError
> {
  return useQuery<InvoiceFilterFacetsOut, ApiError>({
    queryKey: invoiceFilterFacetsKey(),
    queryFn: ({ signal }) => getInvoiceFilterFacets({ signal }),
    staleTime: 5 * 60 * 1000,
  })
}

export function installmentDefaultsKey() {
  return ["installment-defaults"] as const
}

/**
 * Fetch the global installment defaults (currently just the backup-card
 * requirement) so the create form can render the per-invoice checkbox at
 * the right default before a deal prefill runs. The value changes rarely
 * (an operator flips a switch), so cache it for the session.
 */
export function useInstallmentDefaults(
  options: { enabled?: boolean } = {},
): UseQueryResult<InstallmentDefaultsOut, ApiError> {
  return useQuery<InstallmentDefaultsOut, ApiError>({
    queryKey: installmentDefaultsKey(),
    queryFn: ({ signal }) => getInstallmentDefaults({ signal }),
    // The reissue/edit path seeds the checkbox from the plan, not the
    // global, so the caller disables the fetch there (it would be discarded).
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  })
}

// The prefill is fetched imperatively from
// ``useDealSearchSelection.selectDeal`` via ``queryClient.fetchQuery``
// — no React component subscribes to it, so there's no
// ``useDealPrefill`` hook. The key is exported because the selection
// hook needs it for the cache write.
export function dealPrefillKey(dealId: string) {
  return ["hubspot", "deal-prefill", dealId] as const
}

export function useOpenInvoicesForDeal(
  dealId: string,
  excludeInvoiceId?: string,
): UseQueryResult<UseOpenInvoicesForDealData, ApiError> {
  const trimmed = dealId.trim()
  return useQuery<UseOpenInvoicesForDealData, ApiError>({
    // The excluded id is part of the key: the create form's in-progress
    // draft only exists after Review, so the same deal yields a
    // different result depending on whether we're discounting it.
    queryKey: [...invoicesByDealKey(trimmed), excludeInvoiceId ?? null],
    queryFn: async ({ signal }) => {
      const response = await listInvoices(
        {
          hubspot_deal_id: trimmed,
          status: OPEN_INVOICE_STATUSES,
          // A deal realistically has <= 2-3 open invoices; 25 covers any
          // plausible case in one round-trip and lets us branch on each
          // row's status. The cap can only ever drop *extra* voidable
          // rows, never change the resolution (design §4.1).
          limit: 25,
        },
        { signal },
      )
      // Discount the form's own in-progress draft (post-Review) so the
      // form never prompts about the very invoice it just created.
      const candidates = response.items.filter(
        (item) => item.id !== excludeInvoiceId,
      )
      // One pass, classifying each candidate at most once: a voidable row
      // wins outright (void wins over go-to when both buckets are
      // present, design OQ-1); otherwise the first sendable row is the
      // go-to target.
      let target: (typeof candidates)[number] | undefined
      let resolution: DealInvoiceResolution = "none"
      for (const item of candidates) {
        const bucket = classifyDealInvoiceStatus(item.status)
        if (bucket === "void") {
          target = item
          resolution = "void_existing"
          break
        }
        if (bucket === "open" && !target) {
          target = item
          resolution = "open_existing"
        }
      }
      const existing: ExistingDealInvoice | null = target
        ? {
            id: target.id,
            status: target.status,
            createdById: target.created_by.id,
          }
        : null
      return {
        resolution,
        existing,
        hasOpenInvoice: resolution !== "none",
        count: response.count,
      }
    },
    enabled: trimmed.length > 0,
    staleTime: 30_000,
  })
}

export function useCreateInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  CreateInvoiceIn
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, CreateInvoiceIn>({
    mutationFn: (body) => createInvoice(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export interface ResendInvoiceVars {
  id: string
  // Optional deposit leg to resend. Required by the backend for deposit
  // invoices; omitted (and ignored) for full/installment invoices.
  stage?: "deposit" | "balance"
}

export function useResendInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  ResendInvoiceVars
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, ResendInvoiceVars>({
    mutationFn: ({ id, stage }) => resendInvoice(id, stage),
    onSuccess: (detail) => {
      queryClient.setQueryData(
        invoiceDetailKey(detail.id),
        toFrontendInvoiceDetail(detail),
      )
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export interface AmendInvoiceVars {
  id: string
  body: AmendInvoiceIn
}

/**
 * Amend the discounts on a sent-but-unpaid invoice. On success the
 * server returns the refreshed detail (new discounts + net) and has
 * emailed the customer the "invoice updated" notification; prime the
 * detail cache and invalidate the list + this invoice's email-preview
 * so every surface reflects the new amount.
 */
export function useAmendInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  AmendInvoiceVars
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, AmendInvoiceVars>({
    mutationFn: ({ id, body }) => amendInvoice(id, body),
    onSuccess: (detail) => {
      queryClient.setQueryData(
        invoiceDetailKey(detail.id),
        toFrontendInvoiceDetail(detail),
      )
      queryClient.invalidateQueries({ queryKey: emailPreviewKey(detail.id) })
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export interface UpdateDraftInvoiceVars {
  id: string
  body: UpdateInvoiceIn
}

export function useUpdateDraftInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  UpdateDraftInvoiceVars
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, UpdateDraftInvoiceVars>({
    mutationFn: ({ id, body }) => updateInvoice(id, body),
    onSuccess: (detail) => {
      // useInvoice's queryFn maps the DTO to the frontend shape
      // (nested `customer`); priming the cache with the raw DTO
      // would make the next read crash on `customer.name`.
      queryClient.setQueryData(
        invoiceDetailKey(detail.id),
        toFrontendInvoiceDetail(detail),
      )
      queryClient.invalidateQueries({ queryKey: emailPreviewKey(detail.id) })
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export interface SendInvoiceVars {
  id: string
}

export function useSendInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  SendInvoiceVars
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, SendInvoiceVars>({
    mutationFn: ({ id }) => sendInvoice(id),
    onSuccess: (detail) => {
      queryClient.setQueryData(
        invoiceDetailKey(detail.id),
        toFrontendInvoiceDetail(detail),
      )
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export interface FinalizeInvoiceVars {
  id: string
}

export function useFinalizeInvoice(): UseMutationResult<
  InvoiceDetailOut,
  ApiError,
  FinalizeInvoiceVars
> {
  const queryClient = useQueryClient()
  return useMutation<InvoiceDetailOut, ApiError, FinalizeInvoiceVars>({
    mutationFn: ({ id }) => finalizeInvoice(id),
    onSuccess: (detail) => {
      queryClient.setQueryData(
        invoiceDetailKey(detail.id),
        toFrontendInvoiceDetail(detail),
      )
      queryClient.invalidateQueries({ queryKey: ["invoices"] })
    },
  })
}

export function emailPreviewKey(id: string | null) {
  return ["invoices", "email-preview", id] as const
}

export interface UseEmailPreviewOptions {
  enabled?: boolean
}

export function useEmailPreview(
  id: string | null,
  options: UseEmailPreviewOptions = {},
): UseQueryResult<EmailPreviewOut, ApiError> {
  const { enabled = true } = options
  return useQuery<EmailPreviewOut, ApiError>({
    queryKey: emailPreviewKey(id),
    queryFn: ({ signal }) => fetchEmailPreview(id as string, { signal }),
    enabled: enabled && id != null,
    staleTime: 30_000,
  })
}

// Canonical Planna markets that allow installment plans (slice 03
// country gate). The backend's `_JURISDICTION_FOR_MARKET` lookup is
// the source of truth — if a new market lands there, mirror it here.
const INSTALLMENT_ELIGIBLE_MARKETS = new Set(["US", "UK"])

export interface UseInstallmentGateResult {
  eligible: boolean
}

/**
 * Country gate for the Installment Payments form section.
 *
 * Returns whether the selected cohort's market allows an installment
 * plan and, as a side effect, resets the installment form fields when the
 * gate transitions to closed while installments is the selected mode.
 * Without the reset, a rep who selected installments while picking a US
 * cohort and then switched to APAC would silently submit
 * `payment_mode="installments"` even though the picker had been hidden;
 * the mode falls back to "full" and count/cadence reset to defaults.
 *
 * Deposit is available in all markets (no jurisdiction gate), so the
 * reset only fires for the `installments` mode — a deposit selection is
 * left untouched when the gate closes.
 */
export function useInstallmentGate(
  marketName: string | null,
  form: UseFormReturn<CreateInvoiceForm>,
): UseInstallmentGateResult {
  const eligible =
    marketName !== null && INSTALLMENT_ELIGIBLE_MARKETS.has(marketName)

  // Tracks the previous `eligible` value so the reset below can tell a
  // genuine eligible→ineligible transition (the rep switched markets
  // mid-session) from a form that simply mounted already-ineligible.
  // `null` until the first effect run.
  const prevEligibleRef = React.useRef<boolean | null>(null)

  React.useEffect(() => {
    const prevEligible = prevEligibleRef.current
    prevEligibleRef.current = eligible
    if (eligible) return
    const values = form.getValues()
    // Only installments is jurisdiction-gated. A deposit selection (any
    // market) and the count/cadence defaults are left as-is unless the
    // mode itself is the now-illegal installments.
    const needsReset =
      values.paymentMode === "installments" ||
      values.installmentCount !== "4" ||
      values.installmentCadence !== "monthly"
    if (!needsReset) return
    // Only a real user-driven transition (was eligible, now isn't) should
    // dirty the form. A mount-time correction on an already-ineligible
    // form must use shouldDirty:false — otherwise an untouched edit form
    // for a now-ineligible plan is silently marked dirty, blocking Send
    // and tripping the unsaved-changes guard.
    const shouldDirty = prevEligible === true
    if (values.paymentMode === "installments") {
      form.setValue("paymentMode", "full", { shouldDirty })
    }
    form.setValue("installmentCount", "4", { shouldDirty })
    form.setValue("installmentCadence", "monthly", { shouldDirty })
    form.clearErrors([
      "paymentMode",
      "installmentCount",
      "installmentCadence",
    ])
  }, [eligible, form])

  return { eligible }
}
